import React, { useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, BrainCircuitIcon, ImagesIcon, SparklesIcon } from 'lucide-react';
import { ImageData } from '../utils/mockData';
import { motion, useAnimation, useInView } from 'framer-motion';
interface AssetAnalysisProps {
  data: ImageData[];
}
export const AssetAnalysis: React.FC<AssetAnalysisProps> = ({
  data
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isNextHovered, setIsNextHovered] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [sliderPosition, setSliderPosition] = useState(50); // 0-100 percentage
  const [isDragging, setIsDragging] = useState(false);
  const [exporting, setExporting] = useState(false);
  // While the PDF is being generated, switch from the interactive single-asset
  // carousel to a static block that shows all assets with full-opacity text.
  useEffect(() => {
    const start = () => setExporting(true);
    const end = () => setExporting(false);
    window.addEventListener('report:export-start', start);
    window.addEventListener('report:export-end', end);
    return () => {
      window.removeEventListener('report:export-start', start);
      window.removeEventListener('report:export-end', end);
    };
  }, []);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const insightsRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(insightsRef, {
    once: true,
    amount: 0.3
  });
  const controls = useAnimation();
  const selectedAsset = data[selectedIndex];
  useEffect(() => {
    if (isInView) {
      controls.start({
        x: 0,
        opacity: 1,
        transition: {
          duration: 0.8,
          ease: 'easeOut'
        }
      });
    }
  }, [isInView, controls]);
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStart(touch.clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const touch = e.touches[0];
    const currentTouch = touch.clientX;
    const diff = touchStart - currentTouch;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextAsset();
      } else {
        prevAsset();
      }
      setTouchStart(null);
    }
  };
  // Slider drag handlers
  const handleSliderMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    updateSliderPosition(e.clientX);
  };
  const handleSliderTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    updateSliderPosition(e.touches[0].clientX);
  };
  const updateSliderPosition = (clientX: number) => {
    if (!videoContainerRef.current) return;
    const rect = videoContainerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, x / rect.width * 100));
    setSliderPosition(percentage);
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        updateSliderPosition(e.clientX);
      }
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging) {
        updateSliderPosition(e.touches[0].clientX);
      }
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);
  const getAIFeedback = (asset: ImageData) => {
    switch (asset.id) {
      case 1:
        return {
          summary: 'What works (and why):',
          details: [<span key="1" data-id="element-9">
              This asset leverages strong luminance contrast and central
              positioning to drive initial visual pickup. The aspirational
              lifestyle setting creates a cohesive visual narrative that
              attracts the eye effectively across both segments.
            </span>, <span key="2" data-id="element-10">
              <strong className="text-[#101010]" data-id="element-11">Segment difference:</strong>{' '}
              Attention drivers explain initial visual pickup in both segments,
              but behavioural interaction within the tech enthusiast segment
              stayed low, the visual attracts the eye yet the message does not
              convert attention into engagement for a spec-driven audience.
            </span>]
        };
      case 2:
        return {
          summary: 'What works (and why):',
          details: [<span key="1" data-id="element-12">
              The celebrity-style endorsement feel utilizes face recognition, a
              powerful attention blueprint driver. Faces naturally draw the eye,
              ensuring rapid initial fixation.
            </span>, <span key="2" data-id="element-13">
              <strong className="text-[#101010]" data-id="element-14">Segment difference:</strong>{' '}
              While the face draws attention, the secondary product placement
              fails to resonate strongly with the tech enthusiast segment,
              leading to a drop-off in sustained engagement compared to the
              lifestyle audience.
            </span>]
        };
      case 3:
        return {
          summary: 'What works (and why):',
          details: [<span key="1" data-id="element-15">
              High contrast product framing plus central size and position drive
              strong pickup. The clear orientation and sharp edges of the device
              ensure it stands out immediately against the background.
            </span>, <span key="2" data-id="element-16">
              <strong className="text-[#101010]" data-id="element-17">Segment difference:</strong>{' '}
              High contrast product framing plus central size and position drive
              strong pickup, and message-audience fit converts that attention
              into significantly higher interaction within the tech enthusiast
              segment.
            </span>]
        };
      case 4:
        return {
          summary: 'What works (and why):',
          details: [<span key="1" data-id="element-18">
              The performance and price-to-performance framing utilizes clear
              text orientation and high color saturation to guide the eye
              directly to the value proposition.
            </span>, <span key="2" data-id="element-19">
              <strong className="text-[#101010]" data-id="element-20">Segment difference:</strong>{' '}
              This spec-heavy approach performs well with tech enthusiasts who
              are actively seeking this information, but it fails to capture the
              sustained attention of the lifestyle segment, who prioritize
              aspirational imagery.
            </span>]
        };
      default:
        return {
          summary: 'Analysis not available',
          details: []
        };
    }
  };
  const nextAsset = () => {
    setSelectedIndex(prev => (prev + 1) % data.length);
  };
  const prevAsset = () => {
    setSelectedIndex(prev => (prev - 1 + data.length) % data.length);
  };
  const getNextAssetName = () => {
    const nextIndex = (selectedIndex + 1) % data.length;
    return data[nextIndex].name;
  };
  const getProbability = (id: number) => id === 1 ? '0.1' : id === 2 ? '0.2' : id === 3 ? '91.2' : '8.5';
  const getLifestyleProbability = (id: number) => id === 1 ? '84.6' : id === 2 ? '12.1' : id === 3 ? '0.9' : '2.4';
  const feedback = getAIFeedback(selectedAsset);
  return <div className="space-y-6" data-id="element-21">
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-22">
        <div className="flex items-start space-x-3" data-id="element-23">
          <BrainCircuitIcon className="h-5 w-5 text-[#C00000] mt-1 flex-shrink-0" data-id="element-24" />
          <div data-id="element-25">
            <h3 className="font-medium text-[#101010] mb-2" data-id="element-26">
              Detailed Asset Analysis
            </h3>
            <p className="text-sm text-[#404040] leading-relaxed" data-id="element-27">
              Each asset has been analysed using visual behavioural data to
              identify areas of high engagement and attention. The data reveals
              how users interact with different elements of each image in a
              natural viewing environment, highlighting focal points that drive
              stopping power. This behavioural approach allows us to understand
              why certain visuals perform better and provides actionable
              insights for your creative decisions.
            </p>
          </div>
        </div>
      </div>

      <div data-export-hide className="flex flex-col items-center space-y-4" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={() => setTouchStart(null)} data-id="element-28">
        <div className="flex items-center justify-center space-x-4 w-full" data-id="element-29">
          <button onClick={prevAsset} className="p-2 rounded-sm bg-[#C00000]/10 hover:bg-[#C00000]/20 transition-colors shadow-sm" aria-label="Previous asset" data-id="element-30">
            <ChevronLeftIcon className="h-5 w-5 text-[#C00000]" data-id="element-31" />
          </button>
          {data.map((asset, index) => <motion.div key={asset.id} whileHover={{
          y: -4
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 10
        }} className={`cursor-pointer rounded-sm overflow-hidden bg-[#F9FAFB] ${selectedIndex === index ? 'ring-2 ring-[#C00000] ring-offset-2' : 'ring-1 ring-gray-200'}`} onClick={() => setSelectedIndex(index)} data-id="element-32">
              <img src={asset.thumbnailUrl} alt={asset.name} className="w-20 h-12 object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-33" />
            </motion.div>)}
          <button onClick={nextAsset} onMouseEnter={() => setIsNextHovered(true)} onMouseLeave={() => setIsNextHovered(false)} className="p-2 rounded-sm bg-[#C00000]/10 hover:bg-[#C00000]/20 transition-colors shadow-sm" aria-label="Next asset" data-id="element-34">
            <ChevronRightIcon className="h-5 w-5 text-[#C00000]" data-id="element-35" />
          </button>
        </div>
        <div className="text-sm text-[#737373]" data-id="element-36">
          {isNextHovered ? <div className="flex items-center" data-id="element-37">
              <span data-id="element-38">Next: </span>
              <span className="text-[#C00000] font-medium ml-1" data-id="element-39">
                {getNextAssetName()}
              </span>
            </div> : <div className="flex items-center" data-id="element-40">
              <ImagesIcon className="h-3 w-3 mr-1" data-id="element-41" />
              <span data-id="element-42">View all {data.length} assets</span>
            </div>}
        </div>
      </div>

      <div data-export-hide className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-43">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-id="element-44">
          {/* Left Column - Video with Comparison Slider */}
          <div className="flex flex-col" data-id="element-45">
            {/* Video Player with Comparison Slider */}
            <div className="flex flex-col" data-id="element-46">
              {/* Video Display with Slider */}
              <div ref={videoContainerRef} className="relative rounded-sm overflow-hidden bg-[#F9FAFB] mx-auto select-none w-full" style={{
              maxWidth: '560px',
              aspectRatio: '16 / 9',
              cursor: isDragging ? 'grabbing' : 'grab'
            }} onMouseDown={handleSliderMouseDown} onTouchStart={handleSliderTouchStart} data-id="element-47">
                <div className="absolute inset-0" data-id="element-48">
                  {/* Original Image (Left Side) */}
                  <div className="absolute inset-0" data-id="element-49">
                    {selectedAsset.thumbnailUrl ? <img src={selectedAsset.thumbnailUrl} alt={`${selectedAsset.name} original`} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="eager" data-id="element-50" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-51">
                        No thumbnail
                      </div>}
                    <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-sm text-xs font-medium pointer-events-none z-20" data-id="element-52">
                      Original
                    </div>
                  </div>

                  {/* Heatmap Overlay (Right Side) - Clipped */}
                  <div className="absolute inset-0 pointer-events-none" style={{
                  clipPath: `inset(0 0 0 ${sliderPosition}%)`
                }} data-id="element-53">
                    {selectedAsset.heatmapUrl || selectedAsset.thumbnailUrl ? <img src={selectedAsset.heatmapUrl || selectedAsset.thumbnailUrl} alt={`${selectedAsset.name} heatmap`} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="eager" onError={e => {
                    const img = e.currentTarget;
                    if (selectedAsset.thumbnailUrl && img.src !== selectedAsset.thumbnailUrl) {
                      img.src = selectedAsset.thumbnailUrl;
                    }
                  }} data-id="element-54" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-55">
                        No heatmap
                      </div>}
                    <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-sm text-xs font-medium z-20" data-id="element-56">
                      Attention heatmap
                    </div>
                  </div>

                  {/* Vertical Slider Line */}
                  <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg pointer-events-none z-30" style={{
                  left: `${sliderPosition}%`,
                  transform: 'translateX(-50%)'
                }} data-id="element-57">
                    {/* Slider Handle */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center pointer-events-auto cursor-grab active:cursor-grabbing" style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }} data-id="element-58">
                      <div className="flex gap-0.5" data-id="element-59">
                        <div className="w-0.5 h-3 bg-gray-400 rounded-full" data-id="element-60"></div>
                        <div className="w-0.5 h-3 bg-gray-400 rounded-full" data-id="element-61"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="mt-4 text-center" data-id="element-62">
                <p className="text-xs text-gray-500" data-id="element-63">
                  Drag the slider to compare the original creative vs the
                  attention heatmap
                </p>
              </div>

              <div className="mt-3 text-center" data-id="element-64">
                <h4 className="font-medium text-gray-900" data-id="element-65">
                  {selectedAsset.name}
                </h4>
              </div>
            </div>
          </div>

          {/* Right Column - AI Insights */}
          <div className="flex flex-col" ref={insightsRef} data-id="element-66">
            {/* AI Insights - Expanded */}
            <div className="flex flex-col" data-id="element-67">
              <div className="flex items-center space-x-2 mb-4" data-id="element-68">
                <BrainCircuitIcon className="h-5 w-5 text-[#C00000]" data-id="element-69" />
                <motion.h3 className="font-medium text-[#101010] flex items-center" initial={{
                x: 20,
                opacity: 0
              }} animate={controls} data-id="element-70">
                  Attention Insights
                  <motion.span initial={{
                  opacity: 0,
                  scale: 0
                }} animate={isInView ? {
                  opacity: 1,
                  scale: 1
                } : {}} transition={{
                  delay: 0.3,
                  duration: 0.5
                }} className="flex items-center ml-2 bg-[#C00000]/10 text-[#C00000] px-2 py-0.5 rounded-sm text-xs" data-id="element-71">
                    <SparklesIcon className="h-3 w-3 mr-1" data-id="element-72" />
                    Powered by AI
                  </motion.span>
                </motion.h3>
              </div>

              {/* Summary */}
              <motion.div className="mb-6" initial={{
              y: 10,
              opacity: 0
            }} animate={isInView ? {
              y: 0,
              opacity: 1
            } : {}} transition={{
              delay: 0.4,
              duration: 0.5
            }} data-id="element-73">
                <p className="text-[#101010] font-semibold mb-2" data-id="element-74">
                  {feedback.summary}
                </p>
                {feedback.details.length === 1 ? <p className="text-[#404040] leading-relaxed" data-id="element-75">
                    {feedback.details[0]}
                  </p> : <ul className="list-disc pl-5 space-y-2 text-[#404040] leading-relaxed" data-id="element-76">
                    {feedback.details.map((detail, idx) => <li key={idx} data-id="element-77">{detail}</li>)}
                  </ul>}
              </motion.div>

              {/* Detailed Analysis */}

              {/* Key Metrics */}
              <motion.div className="mt-6 space-y-4" initial={{
              y: 20,
              opacity: 0
            }} animate={isInView ? {
              y: 0,
              opacity: 1
            } : {}} transition={{
              delay: 0.8,
              duration: 0.5
            }} data-id="element-78">
                <h4 className="text-sm font-medium text-[#101010]" data-id="element-79">
                  Performance Metrics by segment (Illustrative)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-80">
                  {/* Tech enthusiast segment */}
                  <div className="bg-[#F9FAFB] border border-[#E5E5E5] rounded-sm p-4" data-id="element-81">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#101010] mb-3" data-id="element-82">
                      Tech enthusiast
                    </p>
                    <div className="space-y-3" data-id="element-83">
                      <div className="flex items-baseline justify-between" data-id="element-84">
                        <span className="text-sm text-[#737373]" data-id="element-85">
                          SPR Score
                        </span>
                        <span className="text-lg font-semibold text-[#C00000]" data-id="element-86">
                          {selectedAsset.techEnthusiast.stoppingPower}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between" data-id="element-87">
                        <span className="text-sm text-[#737373]" data-id="element-88">
                          Audience Signals
                        </span>
                        <span className="text-lg font-semibold text-[#101010]" data-id="element-89">
                          {selectedAsset.techEnthusiast.views.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between" data-id="element-90">
                        <span className="text-sm text-[#737373]" data-id="element-91">
                          Probability to be Best
                        </span>
                        <span className="text-lg font-semibold text-[#C00000]" data-id="element-92">
                          {getProbability(selectedAsset.id)}%
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Lifestyle segment */}
                  <div className="bg-[#F9FAFB] border border-[#E5E5E5] rounded-sm p-4" data-id="element-93">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#101010] mb-3" data-id="element-94">
                      Lifestyle
                    </p>
                    <div className="space-y-3" data-id="element-95">
                      <div className="flex items-baseline justify-between" data-id="element-96">
                        <span className="text-sm text-[#737373]" data-id="element-97">
                          SPR Score
                        </span>
                        <span className="text-lg font-semibold text-[#C00000]" data-id="element-98">
                          {selectedAsset.lifestyle.stoppingPower}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between" data-id="element-99">
                        <span className="text-sm text-[#737373]" data-id="element-100">
                          Audience Signals
                        </span>
                        <span className="text-lg font-semibold text-[#101010]" data-id="element-101">
                          {selectedAsset.lifestyle.views.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between" data-id="element-102">
                        <span className="text-sm text-[#737373]" data-id="element-103">
                          Probability to be Best
                        </span>
                        <span className="text-lg font-semibold text-[#C00000]" data-id="element-104">
                          {getLifestyleProbability(selectedAsset.id)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Export-only layout: all assets with full-opacity text + both images.
                        Rendered only while generating the PDF so every asset and its
                        insights appear, unaffected by scroll-based animations. */}
      {exporting && <div className="space-y-6" data-id="element-105">
          {data.map(asset => {
        const assetFeedback = getAIFeedback(asset);
        return <div key={asset.id} className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5]" data-id="element-106">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-id="element-107">
                  {/* Images: original + heatmap side by side */}
                  <div className="flex flex-col" data-id="element-108">
                    <div className="grid grid-cols-2 gap-3" data-id="element-109">
                      <div className="relative rounded-sm overflow-hidden bg-[#F9FAFB] border border-[#E5E5E5]" data-id="element-110">
                        <img src={asset.thumbnailUrl} alt={`${asset.name} original`} className="w-full h-full object-cover" style={{
                    aspectRatio: '16 / 9'
                  }} referrerPolicy="no-referrer" data-id="element-111" />
                        <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-0.5 rounded-sm text-[10px] font-medium" data-id="element-112">
                          Original
                        </div>
                      </div>
                      <div className="relative rounded-sm overflow-hidden bg-[#F9FAFB] border border-[#E5E5E5]" data-id="element-113">
                        <img src={asset.heatmapUrl || asset.thumbnailUrl} alt={`${asset.name} attention heatmap`} className="w-full h-full object-cover" style={{
                    aspectRatio: '16 / 9'
                  }} referrerPolicy="no-referrer" data-id="element-114" />
                        <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-0.5 rounded-sm text-[10px] font-medium" data-id="element-115">
                          Attention heatmap
                        </div>
                      </div>
                    </div>
                    <h4 className="mt-3 text-center font-medium text-gray-900" data-id="element-116">
                      {asset.name}
                    </h4>
                  </div>

                  {/* Insights + metrics (static, full opacity) */}
                  <div className="flex flex-col" data-id="element-117">
                    <div className="flex items-center space-x-2 mb-4" data-id="element-118">
                      <BrainCircuitIcon className="h-5 w-5 text-[#C00000]" data-id="element-119" />
                      <h3 className="font-medium text-[#101010]" data-id="element-120">
                        Attention Insights
                      </h3>
                    </div>
                    <p className="text-[#101010] font-semibold mb-2" data-id="element-121">
                      {assetFeedback.summary}
                    </p>
                    {assetFeedback.details.length === 1 ? <p className="text-[#404040] leading-relaxed" data-id="element-122">
                        {assetFeedback.details[0]}
                      </p> : <ul className="list-disc pl-5 space-y-2 text-[#404040] leading-relaxed" data-id="element-123">
                        {assetFeedback.details.map((detail, idx) => <li key={idx} data-id="element-124">{detail}</li>)}
                      </ul>}
                    <h4 className="mt-6 mb-3 text-sm font-medium text-[#101010]" data-id="element-125">
                      Performance Metrics by segment (Illustrative)
                    </h4>
                    <div className="grid grid-cols-2 gap-3" data-id="element-126">
                      <div className="bg-[#F9FAFB] border border-[#E5E5E5] p-4 rounded-sm" data-id="element-127">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#101010] mb-3" data-id="element-128">
                          Tech enthusiast
                        </p>
                        <div className="space-y-2" data-id="element-129">
                          <div className="flex items-baseline justify-between" data-id="element-130">
                            <span className="text-xs text-[#737373]" data-id="element-131">
                              SPR Score
                            </span>
                            <span className="text-base font-semibold text-[#C00000]" data-id="element-132">
                              {asset.techEnthusiast.stoppingPower}
                            </span>
                          </div>
                          <div className="flex items-baseline justify-between" data-id="element-133">
                            <span className="text-xs text-[#737373]" data-id="element-134">
                              Audience Signals
                            </span>
                            <span className="text-base font-semibold text-[#101010]" data-id="element-135">
                              {asset.techEnthusiast.views.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-baseline justify-between" data-id="element-136">
                            <span className="text-xs text-[#737373]" data-id="element-137">
                              Probability to be Best
                            </span>
                            <span className="text-base font-semibold text-[#C00000]" data-id="element-138">
                              {getProbability(asset.id)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-[#F9FAFB] border border-[#E5E5E5] p-4 rounded-sm" data-id="element-139">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#101010] mb-3" data-id="element-140">
                          Lifestyle
                        </p>
                        <div className="space-y-2" data-id="element-141">
                          <div className="flex items-baseline justify-between" data-id="element-142">
                            <span className="text-xs text-[#737373]" data-id="element-143">
                              SPR Score
                            </span>
                            <span className="text-base font-semibold text-[#C00000]" data-id="element-144">
                              {asset.lifestyle.stoppingPower}
                            </span>
                          </div>
                          <div className="flex items-baseline justify-between" data-id="element-145">
                            <span className="text-xs text-[#737373]" data-id="element-146">
                              Audience Signals
                            </span>
                            <span className="text-base font-semibold text-[#101010]" data-id="element-147">
                              {asset.lifestyle.views.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-baseline justify-between" data-id="element-148">
                            <span className="text-xs text-[#737373]" data-id="element-149">
                              Probability to be Best
                            </span>
                            <span className="text-base font-semibold text-[#C00000]" data-id="element-150">
                              {getLifestyleProbability(asset.id)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>;
      })}
        </div>}
    </div>;
};